/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { parseISO, format } from "date-fns";
import { FAVOURITE_ITEM, INVENTORY_SECTION } from "../../constants/lang";
import StyledInventoryAction from "./StyledInventoryAction";
import CollapsibleTable from "../../components/CommonComponents/CollapsibleTable";
import { orderManagementData } from "../../components/CommonComponents/CollapsibleTable/dataFunctions";
import { useOrderManagemnt } from "../../hooks/common-hooks/useOrderManagemnt";
import NoDataFound from "./NoDataFound";
import ShimmerLoader from "../../components/CommonComponents/Loader/ShimmerLoader";
import { ROUTE_PATH } from "../../constants/route";
import { toTwoDecimalPlaces } from "../../utils/commonFunctions";
import DoneIcon from "@mui/icons-material/Done";
import { fetchMarkShipped } from "../../store/reducers/createProfile";
import { useDispatch } from "react-redux";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import StatusTag from "../../components/CommonComponents/StatusTag";
import rollbar from "../../rollbarConfig";
import wanderobelogo from "../../assets/images/wanderobe-logo-1.svg";

const InventoryActionTable = () => {
  const dispatch = useDispatch();
  const { products, loading, searchProducts } = useOrderManagemnt(5);
  const headings = [
    <div style={{ paddingLeft: "24%" }}>Order no.</div>,
    "Order placed at",
    "Ship by",
    "Total Items",
    "Pending amount",
    "Action",
  ];

  const detailsHeading = [
    "Product name",
    "Size/Color",
    "Price",
    "Qty.",
    "Total amount"
  ];

  const MarkToShip = async(id) => {
    try{
      const res = await dispatch(fetchMarkShipped(id));
      const { status, message } = res?.payload || {};
      if(status){
        ToastNotifySuccess("Item is marked as shipped");
        searchProducts();
      } else {
        ToastNotifyError(message);
      }
    } catch(err){
      console.log("err :", err)
      rollbar.error("Error message", err);
    }
  }

  // Memoize processed product rows for performance optimization
  const invoiceRows = useMemo(
    () =>
      products?.slice(0, 5).map((item) =>
        orderManagementData(
          `#${item?.order_no}`,
          item?.creation_date
            ? format(parseISO(item?.creation_date), "d MMM yyyy, h:mm a")
            : "--",
          item?.arrival_date
            ? format(parseISO(item?.arrival_date), "d MMM yyyy")
            : "--",
          item?.order_items?.length || "0",
          `$${toTwoDecimalPlaces(item?.price_total) || "0.00"}`,
          item?.seller_status.toLowerCase() === "ship to wanderobe"  ? 
          <StatusTag status={item?.seller_status} /> :          
          (item?.action?.toLowerCase() === "pending from logistics") ? 
            <Box>
              <Button variant='contained' color='primary' className='action-btn' onClick={() => MarkToShip(item?.id)}>
                <DoneIcon sx={{ fontSize: "16px", color: "white" }} />
                <Typography className='action-text'>Ready to ship</Typography>
              </Button>
            </Box> : 
            <StatusTag status={item?.action} />,
          item?.order_items?.map((order) => ({
            productName: (
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box
                  component='img'
                  src={order?.product_image?.[0] || wanderobelogo}
                  sx={{
                    height: 40,
                    width: 40,
                    objectFit: "contain",
                    border: "0.62px solid #CBCBCB",
                  }}
                />
                <Box display='flex' gap='4px' flexDirection='column'>
                  <Typography
                    className='text-truncate'
                    fontSize={{ xs: "12px", md: "14px" }}
                  >
                    {order?.product_name || "--"}
                  </Typography>
                  <Box
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      alignItems: "center",
                      fontSize: "12px",
                      color: "#656565",
                    }}
                  >
                    {order?.size || "--"} /{" "}
                    <Box
                      sx={{
                        height: "14px",
                        width: "14px",
                        background: order?.color_code,
                        marginLeft: "4px",
                        marginRight: "3px",
                        border: "0.1px solid lightgrey",
                      }}
                    />
                    {order?.color || "--"}
                  </Box>
                </Box>
              </Box>
            ),
            size_color: (
              <Box
                sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}
              >
                {order?.size || "--"} /{" "}
                <Box
                  sx={{
                    height: "14px",
                    width: "14px",
                    background: order?.color_code,
                    marginLeft: "4px",
                    marginRight: "3px",
                    border: "0.1px solid lightgrey",
                  }}
                />
                {order?.color || "--"}
              </Box>
            ),
            price: `$${order?.price_per_unit || "0.00"}`,
            quantity: `x${order?.quantity || 0}`,
            totalAmount: `$${order?.total_price || "0.00"}`,
          })) || []
        )
      ) || [],
      // eslint-disable-next-line react-hooks/exhaustive-deps
    [products]
  );

    return (
        <StyledInventoryAction>
            <Box className="head-container">
                <Typography className="main-title">{INVENTORY_SECTION.title}</Typography>
                {products?.length > 0 && <Link to={ROUTE_PATH.ORDER_MANAGEMENT} className="view-all-btn">{FAVOURITE_ITEM.button}</Link>}
            </Box>
            <Box className="main-container-table">
                {loading ?
                    <div className="loader-container">
                        <ShimmerLoader />
                    </div>
                    : products?.length > 0 ? (
                        <CollapsibleTable
                            sxHeight="fit-content"
                            showPagination={false}
                            headings={headings}
                            rows={invoiceRows}
                            isDownIconVisible={true}
                            totalCount={products?.length}
                            detailsHeading={detailsHeading}
                        />
                    ) : (
                        <NoDataFound
                            icon={<ShoppingBagOutlinedIcon className="icon-no-data" />}
                            heading="Nothing Here Yet!"
                            subHeading="Your inventory list will appear once you add products."
                        />
                    )}
            </Box>
        </StyledInventoryAction>
    );
};

export default InventoryActionTable;
